import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { InquiryForm } from '../types';
import { useInquiryStore } from '../store/inquiryStore';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import api from '../config/axios';
import { BlogPost } from '../types';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const uiId = searchParams.get('ui');
  const { addInquiry } = useInquiryStore();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<InquiryForm>({
    defaultValues: {
      service: uiId ? `UI Template #${uiId}` : '',
    },
  });

  useEffect(() => {
    // Fetch blog posts (mock for now, you'll need to create the API endpoint)
    // api.get('/api/blog').then((res) => setBlogPosts(res.data.slice(0, 3)));
    setBlogPosts([
      {
        id: 1,
        title: 'Getting Started with Freelancing',
        excerpt: 'Tips for Filipino freelancers starting their journey...',
        slug: 'getting-started-freelancing',
        created_at: '2024-01-15',
      },
      {
        id: 2,
        title: 'Building Your Portfolio',
        excerpt: 'How to create a standout portfolio that gets clients...',
        slug: 'building-your-portfolio',
        created_at: '2024-02-10',
      },
      {
        id: 3,
        title: 'Automation for VAs',
        excerpt: 'Zapier workflows that save time and increase productivity...',
        slug: 'automation-for-vas',
        created_at: '2024-03-05',
      },
    ]);
  }, []);

  const onSubmit = async (data: InquiryForm) => {
    try {
      await api.post('/api/inquiries', data);
      await addInquiry(data);
      toast.success('Message sent successfully!');
      reset();
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    }
  };

  return (
    <>
      <Helmet>
        <title>AlonForge Solutions | Contact</title>
        <meta
          name="description"
          content="Get in touch with AlonForge Solutions for your IT needs"
        />
      </Helmet>

      <div className="min-h-screen bg-slate py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-12">
            <h1 className="text-4xl font-bold mb-8 text-navy font-heading">
              Get In Touch
            </h1>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <input
                  {...register('name', { required: 'Name is required' })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-skyblue focus:border-transparent"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  {...register('email', { required: 'Email is required' })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-skyblue focus:border-transparent"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Service</label>
                <select
                  {...register('service')}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-skyblue focus:border-transparent"
                >
                  <option value="">Select a service (optional)</option>
                  <option value="Portfolio Website">Portfolio Website</option>
                  <option value="Automation Setup">Automation Setup</option>
                  <option value="SEO Audit">SEO Audit</option>
                  <option value="UI Template">UI Template</option>
                  <option value="IT System">IT System</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <textarea
                  {...register('message', { required: 'Message is required' })}
                  rows={6}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-skyblue focus:border-transparent"
                />
                {errors.message && (
                  <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                )}
              </div>
              <button
                type="submit"
                className="w-full bg-navy text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition-colors font-semibold"
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Blog Teaser */}
          <div>
            <h2 className="text-3xl font-bold mb-8 text-navy font-heading">Latest Blog Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {blogPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition-shadow"
                >
                  <h3 className="text-xl font-semibold mb-2 text-navy font-heading">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{post.excerpt}</p>
                  <span className="text-skyblue text-sm font-semibold">
                    Read more →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;

